import { validationProblemsTag } from '../../../apis/apiValidationProblems';
import { apiSlice, TemplateOrCalculation } from '../../../apis/api';
import { CalculationsCollection, TemplatesCollection } from '../../../swagger/collections';
import {
  ApiAction,
  ApiOperationType,
  CategoryFieldConfigurationResponse,
  ConfigurationFieldType,
  SkuType,
  ValidationArea,
} from '@ydistri/api-sdk';
import { signalrClient } from '../../../signalr/client';
import { createDebugLog, EntityListConfiguration } from '@ydistri/utils';
import { SignalConfigurationChanged } from '../../../signalr/signalrInterfaces';
import { getTags } from '../../../apis/apiLib';
import { entityListTypeForConfiguration } from '../../../screens/Configuration/ConfigurationCategories/SegmentPotentialDefinition/ConfigurationEntityLists/entityListLib';
import { apiTemplate } from '../../../screens/Configuration/apiTemplate';
import { RootState } from '@reduxjs/toolkit/query';
import { parseRootCategoryConfiguration } from '../../../helpers/functions/parseRootCategoryConfiguration';
import {
  checkRequirementsForSpecialConfiguration,
  hiddenObsoleteConfigurationFieldType,
  specialConfigurationsArray,
} from '../../../screens/Configuration/ConfigurationCategories/SegmentPotentialDefinition/segmentPotentialDefinitionLib';
import { createCategoryTree, isRootCategory } from '../../../lib/categories/categoriesLib';
import { apiGlobalConfiguration } from '../../../apis/apiGlobalConfiguration';
import {
  CategoryTree,
  CategoryTreeNode,
  DeleteCategoryConfigurationPayload,
  DeleteEntityListConfigurationPayload,
  PartialException,
  ResetCategoryConfigurationsPayload,
} from '../../../lib/categories/categoryTypes';
import { ROOT_CATEGORY_ID } from '../_constants';
import { capitalizeFirstLetter } from '@ydistri/generic-utils';

const debugLog = createDebugLog('apiCategoryTree');

const { TAGS, TAGS_ARRAY } = getTags('categoryTree');

export const apiCategoryTree = apiSlice
  .enhanceEndpoints({ addTagTypes: TAGS_ARRAY })
  .injectEndpoints({
    endpoints: builder => ({
      getCategoryTree: builder.query<CategoryTree, TemplateOrCalculation>({
        queryFn: async arg => {
          const { data: categoryData } = await (arg.type === 'Template'
            ? TemplatesCollection.getTemplateCategories(arg.id)
            : CalculationsCollection.getCalculationCategories(arg.id));
          const { data: exceptionData } = await (arg.type === 'Template'
            ? TemplatesCollection.getTemplateDeviations(arg.id)
            : CalculationsCollection.getCalculationDeviations(arg.id));

          return { data: createCategoryTree(categoryData.data, exceptionData.data) };
        },
        providesTags: [TAGS.categoryTree],
        async onCacheEntryAdded(templateOrCalculation, api) {
          const { id: selectedTemplateId } = templateOrCalculation;
          try {
            await api.cacheDataLoaded;

            const queryOptions = {
              forceRefetch: false,
              subscriptionOptions: {
                pollingInterval: 0,
              },
            };

            /**
             * This is for handling of exceptions to "hidden" configurations.
             */
            api.dispatch(
              apiTemplate.endpoints.getRootCategoryConfiguration.initiate(
                templateOrCalculation,
                queryOptions,
              ),
            );

            /* =================== */

            // eslint-disable-next-line @typescript-eslint/no-explicit-any -- rtk typings
            const handleHiddenConfigurationExceptions = (state: RootState<any, any, 'api'>) => {
              const rootCategoryConfiguration = parseRootCategoryConfiguration(
                apiTemplate.endpoints.getRootCategoryConfiguration.select(templateOrCalculation)(
                  state,
                ).data,
              );

              const calculationConfiguration =
                apiGlobalConfiguration.endpoints.getCalculationConfiguration.select(
                  templateOrCalculation,
                )(state).data;

              const hiddenSpecialConfigurations = specialConfigurationsArray
                .filter(
                  sc =>
                    !checkRequirementsForSpecialConfiguration(
                      sc,
                      rootCategoryConfiguration,
                      !!calculationConfiguration?.ptRequestListEntityList,
                    ),
                )
                .concat(hiddenObsoleteConfigurationFieldType);

              api.updateCachedData(draft => {
                const rootCategory = draft[ROOT_CATEGORY_ID];
                if (rootCategory) {
                  const stack = [rootCategory.id];

                  while (stack.length > 0) {
                    const categoryId = stack.pop();
                    if (categoryId) {
                      const category = draft[categoryId];
                      if (category) {
                        //we ignore exceptions from hidden configuration fields
                        //first we have to count them, so we can decrease the parent counts later
                        let hiddenExceptionCount = 0;
                        category.exceptions.forEach(e => {
                          e.hidden = hiddenSpecialConfigurations.includes(e.fieldType);
                          if (e.hidden) hiddenExceptionCount++;
                        });
                        category.childExceptionCount = 0;
                        category.children.forEach(childId => {
                          stack.push(childId);
                        });

                        const categoryValidExceptionCount =
                          category.exceptions.length - hiddenExceptionCount;

                        category.slug.split('-').forEach(c => {
                          const cid = parseInt(c);
                          const pCategory = draft[cid];
                          if (pCategory && cid !== categoryId) {
                            pCategory.childExceptionCount += categoryValidExceptionCount;
                          }
                        });
                      }
                    }
                  }
                }
              });
            };

            handleHiddenConfigurationExceptions(api.getState());

            const listener = (signal: SignalConfigurationChanged) => {
              if (selectedTemplateId !== signal.templateId) return;

              if (signal.entityType === 'Category') {
                api.updateCachedData(draft => {
                  const { updates, entityId: categoryId, operationType, apiAction } = signal;
                  // eslint-disable-next-line no-console -- we want this output
                  console.log('SIGNAL [in CategoryTree]', signal);

                  if (isRootCategory(categoryId) && operationType !== ApiOperationType.Replace) {
                    return;
                  }

                  const category = draft[categoryId];
                  if (category) {
                    let exceptionCountDiff = 0;

                    if (operationType === ApiOperationType.Replace) {
                      if (apiAction === ApiAction.DeleteSingleCategoryDeviations) {
                        // eslint-disable-next-line no-console -- we want this output
                        console.log('Deleting single...');
                        exceptionCountDiff -= category.exceptions.length;
                        // eslint-disable-next-line no-console -- we want this output
                        console.log('exceptionCountDiff increased to ', exceptionCountDiff);
                        category.exceptions = [];
                      } else if (apiAction === ApiAction.DeleteSubordinateCategoryDeviations) {
                        // eslint-disable-next-line no-console -- we want this output
                        console.log('Deleting also subcategories...');
                        const resetCategoryExceptions = (c: CategoryTreeNode) => {
                          c.childExceptionCount = 0;
                          exceptionCountDiff -= c.exceptions.length;
                          // eslint-disable-next-line no-console -- we want this output
                          console.log(
                            'exceptionCountDiff changed to ',
                            exceptionCountDiff,
                            'category (',
                            c.id,
                            ')',
                          );
                          c.exceptions = [];
                          c.children.forEach(childId => {
                            // eslint-disable-next-line no-console -- we want this output
                            console.log('Child', childId);
                            const child = draft[childId];
                            if (child) resetCategoryExceptions(child);
                          });
                        };

                        resetCategoryExceptions(category);
                      }
                    } else if (
                      operationType === ApiOperationType.Patch ||
                      operationType === ApiOperationType.Delete
                    ) {
                      updates.forEach(update => {
                        if (update.value !== undefined && update.value !== null) {
                          const split = update.fieldName.split('|').map(capitalizeFirstLetter);
                          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- we know it's a ConfigurationFieldType
                          const fieldType = split[0] as ConfigurationFieldType;
                          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- we know it's a SkuType
                          const skuType = (split[1] === 'Null' ? null : split[1]) as SkuType;

                          if (operationType === ApiOperationType.Patch) {
                            const foundException = category.exceptions.find(
                              e => e.fieldType === fieldType && e.skuType === skuType,
                            );
                            if (foundException) {
                              foundException.value = update.value;
                              // eslint-disable-next-line no-console -- we want this output
                              console.log('exception found, setting to ', update.value);
                            } else {
                              const newException = {
                                skuType,
                                fieldType,
                                value: update.value,
                              };
                              // eslint-disable-next-line no-console -- we want this output
                              console.log('exceptionNotFound, pushing new', newException);
                              category.exceptions.push(newException);
                              exceptionCountDiff++;
                            }
                          } else {
                            category.exceptions = category.exceptions.filter(
                              e => !(e.fieldType === fieldType && e.skuType === skuType),
                            );
                            exceptionCountDiff--;
                          }
                        } else {
                          throw Error(`No value in update!`);
                        }
                      });
                    }

                    /*
                      //For now, this is handled by "handleHiddenConfigurationExceptions" function

                      category.slug.split('-').forEach((c, i) => {
                        const pCategory = draft[parseInt(c)];
                        if (pCategory && i < category.level)
                          pCategory.childExceptionCount += exceptionCountDiff;
                      });
                    */
                  }
                });
              }
              if (signal.entityType === 'EntityListConfiguration') {
                api.updateCachedData(draft => {
                  const { entityId: entityListId } = signal;
                  // eslint-disable-next-line no-console -- we want this output
                  console.log('SIGNAL [in CategoryTree]', signal);
                  const category = draft[1];

                  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Object.keys widens to string[]; the record keys are ConfigurationFieldType
                  const fieldTypes = Object.keys(
                    entityListTypeForConfiguration,
                  ) as ConfigurationFieldType[];

                  if (category) {
                    const deleteEntityListFromExceptions = (c: CategoryTreeNode) => {
                      let exceptionCountDiff = 0;
                      // eslint-disable-next-line typescript/no-unnecessary-condition -- exceptions may be undefined at runtime
                      c.exceptions = c.exceptions.filter(e => {
                        const removeFromFieldType = (
                          ft: ConfigurationFieldType,
                          partialException: PartialException,
                        ) => {
                          if (partialException.fieldType === ft) {
                            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- we know it's a EntityListConfiguration[]
                            const oldConfigs = JSON.parse(
                              partialException.value,
                            ) as EntityListConfiguration[];
                            if (oldConfigs.length > 0) {
                              const newConfigs = oldConfigs.filter(
                                x => x.EntityListId !== entityListId,
                              );
                              if (newConfigs.length !== oldConfigs.length) {
                                partialException.value = JSON.stringify(newConfigs);
                                if (newConfigs.length === 0) {
                                  exceptionCountDiff++;
                                  return false;
                                }
                              }
                            }
                          }
                        };

                        fieldTypes.forEach(fieldType => {
                          removeFromFieldType(fieldType, e);
                        });

                        return true;
                      });
                      let childExceptionCountDiff = 0;
                      c.children.forEach(childId => {
                        const child = draft[childId];
                        if (child) {
                          childExceptionCountDiff = deleteEntityListFromExceptions(child);
                        }
                      });

                      c.childExceptionCount -= childExceptionCountDiff;
                      return exceptionCountDiff + childExceptionCountDiff;
                    };

                    deleteEntityListFromExceptions(category);
                  }
                });
              }
              handleHiddenConfigurationExceptions(api.getState());
            };

            debugLog('API - CategoryTree - onCacheEntryAdded [signalrClient.on]');
            signalrClient.on('TemplateConfigurationChanged', listener);

            api.cacheEntryRemoved.then(() => {
              debugLog('API - CategoryTree - cacheEntryRemoved [signalrClient.off]');
              signalrClient.off('TemplateConfigurationChanged', listener);
            });
          } catch {
            // no-op in case cache entry was removed before data was loaded
          }
          await api.cacheEntryRemoved;
        },
      }),
      resetCategoryConfigurations: builder.mutation<
        CategoryFieldConfigurationResponse[],
        ResetCategoryConfigurationsPayload
      >({
        invalidatesTags: (result, error, { templateId }) => [
          validationProblemsTag({ type: 'Template', id: templateId }, ValidationArea.Categories),
        ],
        queryFn: async ({ templateId, categoryId, apiAction }) => {
          const response = await TemplatesCollection.deleteTemplateCategoryDeviationsApiAction(
            templateId,
            categoryId,
            apiAction,
          );
          return { data: response.data.data };
        },
      }),
      deleteCategoryConfiguration: builder.mutation<undefined, DeleteCategoryConfigurationPayload>({
        invalidatesTags: (result, error, { templateId }) => [
          validationProblemsTag({ type: 'Template', id: templateId }, ValidationArea.Categories),
        ],
        queryFn: async ({ templateId, categoryId, fields }) => {
          await TemplatesCollection.deleteTemplateCategoryConfigurations(
            templateId,
            categoryId,
            fields,
          );
          return { data: undefined };
        },
      }),
      deleteEntityListConfigurations: builder.mutation<
        undefined,
        DeleteEntityListConfigurationPayload
      >({
        invalidatesTags: (result, error, { templateId }) => [
          validationProblemsTag({ type: 'Template', id: templateId }, ValidationArea.Categories),
        ],
        queryFn: async ({ templateId, categoryId, entityListId, configurationFieldType }) => {
          await TemplatesCollection.deleteTemplateCategoryEntityListConfigurations(
            templateId,
            categoryId,
            entityListId,
            configurationFieldType,
          );
          return { data: undefined };
        },
      }),
    }),
  });

export const {
  useGetCategoryTreeQuery,
  useResetCategoryConfigurationsMutation,
  useDeleteCategoryConfigurationMutation,
  useDeleteEntityListConfigurationsMutation,
} = apiCategoryTree;
